import React, { useEffect, useState, Fragment, useMemo, useCallback, useRef } from "react";

import {
  isCC,
  text_truncate,
  arraySort,
} from "../../helpers";
import Table, { HeadsType, RowType } from "../Shared/CustomTable";
import {
  useFiltersState,
  usePlayersDispatch,
  usePlayersState,
  useUserState,
} from "../../store/hooks";
import EditableAlerts from "./EditableAlerts";
import { Alert, AlertSetupType, AlertingPageType, HeadersType, PlayerAlert, SpeedAlertsType } from "./types";
import {
  ACCEL_COUNTS,
  ACCEL_COUNTS_ROWS,
  COUNTS_HEADERS,
  COUNTS_ROWS,
  CUSTOM_ZONE_HEADERS,
  CUSTOM_ZONE_ROWS,
  DECEL_COUNTS,
  DECEL_COUNTS_ROWS,
  DISTANCES_HEADERS,
  DISTANCES_ROWS,
  EMPTY_ARRAY,
  FILTER_KEYS,
  LOAD_METRICS,
  LOAD_METRICS_ROWS,
  SPEED_HEADERS,
  SPEED_ROWS,
} from "config/constants";

import CustomTableFilter from "components/Shared/Filters/CustomTableFilter";
import {
  useCustomEventsDispatch,
  useCustomEventsState,
} from "store/hooks/customEvents";
import { Player } from "config/commonTypes";

export default ({ teamId, league, accountPage }: AlertingPageType) => {
  const {
    loadCustomAlerts,
    saveCustomAlerts,
    loadSpeedAlerts,
    saveUpdateSpeedAlerts,
  } = useCustomEventsDispatch();
  const { customAlerts, speedAlerts } = useCustomEventsState();
  const user = useUserState();
  const { customerPlayers = {} } = usePlayersState();
  const {
    getCustomerPlayers, 
  } = usePlayersDispatch();
  const {
    [FILTER_KEYS.CUSTOM_LIVE_TABLE]: viewCustom = EMPTY_ARRAY,
    [FILTER_KEYS.PLAYERS_ALERT]: filterPlayers = EMPTY_ARRAY,
  } = useFiltersState(
    "LIVE_ALERTING",
    FILTER_KEYS.CUSTOM_LIVE_TABLE,
    FILTER_KEYS.PLAYERS_ALERT
  );

  const [editable, setEditable] = useState(false);
  const [showCustomFilter, setShowCustomFilter] = useState(-1);

  const initialState = {
    headers: [],
    players: [],
  };
  const getInitialAlerting = () => {
    return initialState;
  };
  const [currentAlert, setCurrentAlert] = useState<AlertSetupType>(
    getInitialAlerting()
  );

  const [speedAlertsCopy, setSpeedAlertsCopy] = useState<SpeedAlertsType[]>(speedAlerts.customSpeedAlerts || []);

  const updateAlert = useCallback(
  (playerId: string | number, type: string, patch: Partial<Alert>) => {
    setCurrentAlert(prev => ({
      ...prev,
      players: prev.players.map(p =>
        p.id !== playerId
          ? p // untouched players keep the same reference
          : {
              ...p,
              alerts: p.alerts?.map(a =>
                a.type === type ? { ...a, ...patch } : a
              ),
            }
      ),
    }));
  },
  []
);

const currentAlertRef = useRef(currentAlert);
const speedAlertsRef = useRef(speedAlertsCopy);
currentAlertRef.current = currentAlert;
speedAlertsRef.current = speedAlertsCopy;

const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

const scheduleSave = useCallback((includeSpeed: boolean) => {
  clearTimeout(saveTimeoutRef.current);
  saveTimeoutRef.current = setTimeout(() => {
    if (includeSpeed) {
      saveUpdateSpeedAlerts(user.email, teamId.toString(), speedAlertsRef.current);
    }
    saveCustomAlerts(user.email, teamId.toString(), [
      JSON.stringify(currentAlertRef.current),
    ]);
  }, 3000);
}, [user.email, teamId]);

  useEffect(() => () => clearTimeout(saveTimeoutRef.current), []);

const saveUpdatedData = useCallback(
  (status: boolean, playerId: string | number, alertType: string) => {
    updateAlert(playerId, alertType, { active: status });
    const isSpeed = alertType === "speedMPH";
    if (isSpeed) {
      setSpeedAlertsCopy(prev =>
        prev.map(s =>
          s.nid === playerId && s.stat === alertType ? { ...s, active: status } : s
        )
      );
    }
    scheduleSave(isSpeed);
  },
  [updateAlert, scheduleSave]
);

  useEffect(() => {
    loadCustomAlerts(user.email, teamId.toString());
    loadSpeedAlerts(user.email, teamId.toString());
  }, [teamId]);

  useEffect(() => {
  const teamAlerts = customAlerts.customAlerts?.[teamId];
  const teamPlayers = customerPlayers[teamId];

  if (!teamAlerts || !teamPlayers) return;
  const rosterIds = new Set(teamPlayers.map((p: any) => String(p.key)));
  const savedIds = new Set(teamAlerts.players.map((p: any) => String(p.id)));

  const isOutOfSync =
  rosterIds.size !== savedIds.size ||
  [...rosterIds].some((id) => !savedIds.has(id));

  if (isOutOfSync) {
    const referenceAlerts = teamAlerts.players.find((p: any) => p.alerts?.length > 0)?.alerts || [];

    const newPlayers: PlayerAlert[] = teamPlayers.map((player: any) => {
      const existing = teamAlerts.players.find((p: any) => p.id === player.key);

      const alerts = existing?.alerts || referenceAlerts.map((alert: any) => ({
        value: "",
        type: alert.type,
        active: false,
      }));

      return {
        id: player.key,
        name: player.name,
        jerseyNumber: player.jerseyNumber,
        position: player.position,
        alerts,
      };
    });

    const updatedAlert = {
      ...teamAlerts,
      players: newPlayers,
    };

    saveCustomAlerts(user.email, teamId.toString(), [JSON.stringify(updatedAlert)]);
    setCurrentAlert(updatedAlert);
  } else {
    setCurrentAlert(teamAlerts);
  }
}, [teamId, customAlerts.customAlerts, customerPlayers]);

  useEffect(() => {
    setSpeedAlertsCopy(speedAlerts.customSpeedAlerts);
  }, [speedAlerts.customSpeedAlerts]);

  const handleSaveTableAlert = () => {
    const alerting: string[] = [];
    const alertingTable = JSON.stringify(currentAlert);
    alerting.push(alertingTable);
    setEditable(false);
    saveCustomAlerts(user.email, teamId.toString(), alerting);
    if (speedAlertsCopy.length !== 0) {
      saveUpdateSpeedAlerts(user.email, teamId.toString(), speedAlertsCopy);
    }
  };

  const closeCustom = () => {
    handleCustomHeaders();
    setShowCustomFilter(-1);
  };
  const closeAlert = () => {
    setShowCustomFilter(-1);
  };

  useEffect(() => {
    if(league)
    getCustomerPlayers(teamId, league);
  }, [teamId, league]);

  const customHeaders = useMemo(() => {
  if (!currentAlert || !currentAlert.headers) return [];

  return currentAlert.headers.map((data) => {
    const [group, index] = data.split(".");

    if (group === "0") {
     
         return{
            label: SPEED_HEADERS[+data.split(".")[1]],
            metric: SPEED_ROWS[+data.split(".")[1]],
          };
        
    } else if (group === "1") {
   
         return{
            label: DISTANCES_HEADERS[+data.split(".")[1]],
            metric: DISTANCES_ROWS[+data.split(".")[1]],
          };
        
    } else if (group === "2") {
     
         return{
            label: COUNTS_HEADERS[+data.split(".")[1]],
            metric: COUNTS_ROWS[+data.split(".")[1]],
          };
        
      } else if (group === "3") {
      
         return{
            label: ACCEL_COUNTS[+data.split(".")[1]],
            metric: ACCEL_COUNTS_ROWS[+data.split(".")[1]],
          };
        
      }else if (group === "4") {
      
         return{
            label: DECEL_COUNTS[+data.split(".")[1]],
            metric: DECEL_COUNTS_ROWS[+data.split(".")[1]],
          };
        
      }else if (group === "5") {
     
         return{
            label: CUSTOM_ZONE_HEADERS[+data.split(".")[1]],
            metric: CUSTOM_ZONE_ROWS[+data.split(".")[1]],
          };
        
      }else if (group === "6") {
     
         return{
            label: LOAD_METRICS[+data.split(".")[1]],
            metric: LOAD_METRICS_ROWS[+data.split(".")[1]],
          };
        
      }
      

    return null;
  }).filter((header) => header !== null); // deletes null items
}, [currentAlert.headers]);

  const handleCustomHeaders = () => {
  const currentAlertCopy = JSON.parse(JSON.stringify(currentAlert));
  const customHeadersCopy: any[] = [];
  const currentAlertHeaders: string[] = [];
  const currentHeaders: string[] = [];

  // Maping for fast access to headers and rows based on prefix
  const headersMap: Record<string, { headers: any; rows: any }> = {
    "0": { headers: SPEED_HEADERS, rows: SPEED_ROWS },
    "1": { headers: DISTANCES_HEADERS, rows: DISTANCES_ROWS },
    "2": { headers: COUNTS_HEADERS, rows: COUNTS_ROWS },
    "3": { headers: ACCEL_COUNTS, rows: ACCEL_COUNTS_ROWS },
    "4": { headers: DECEL_COUNTS, rows: DECEL_COUNTS_ROWS },
    "5": { headers: CUSTOM_ZONE_HEADERS, rows: CUSTOM_ZONE_ROWS },
    "6": { headers: LOAD_METRICS, rows: LOAD_METRICS_ROWS },
  };

  viewCustom.forEach((data) => {
    if (!data) return;

    const [prefix, index] = data.name.split(".");
    currentAlertHeaders.push(data.name);

    const group = headersMap[prefix];
    if (group && index in group.headers) {
      const label = group.headers[index];
      const metric = group.rows[index];
      customHeadersCopy.push({ label, metric });
      currentHeaders.push(metric);
    }
  });

  currentAlertCopy.headers = currentAlertHeaders;

  // Adding unexisting alerts
  currentAlertCopy.players.forEach((player: PlayerAlert) => {
    if (!player) return;
    if (!player.alerts) player.alerts = [];
    customHeadersCopy.forEach(({ metric }) => {
      const exists = player.alerts?.some((alert: Alert) => alert.type === metric);
      if (!exists) {
        player.alerts?.push({ value: "", type: metric, active: false });
      }
    });
  });

  // Sorting and filtering alerts according customHeadersCopy
  currentAlertCopy.players.forEach((player: PlayerAlert) => {
    if (!player.alerts) return;

    player.alerts = player.alerts.filter((alert: Alert) =>
      currentHeaders.includes(alert.type)
    );

    player.alerts.sort((a: Alert, b: Alert) => {
      const indexA = customHeadersCopy.findIndex(item => item.metric === a.type);
      const indexB = customHeadersCopy.findIndex(item => item.metric === b.type);
      return indexA - indexB;
    });
  });

  setCurrentAlert(currentAlertCopy);
};
 
const handleDeleteMetric = useCallback((index: number, metric?: string) => {
  if (metric?.includes("speedMPH")) {
    setSpeedAlertsCopy((prev) =>
      prev.map((obj) => (obj.stat === "speedMPH" ? { ...obj, target: 0 } : obj))
    );
  }
  setCurrentAlert((prev) => ({
    ...prev,
    headers: prev.headers.filter((_, i) => i !== index),
    players: prev.players.map((p) => ({
      ...p,
      alerts: p.alerts?.filter((_, i) => i !== index),
    })),
  }));
}, []);

  const handleEditingChange = () => {
    if (editable) {
      setCurrentAlert(customAlerts.customAlerts[teamId]);
      setSpeedAlertsCopy(speedAlerts.customSpeedAlerts);
    }
    setEditable(!editable);
  };

  const addOrUpdateSpeed = (speedAlert: SpeedAlertsType) => {
    setSpeedAlertsCopy((prevSpeedAlertsCopy) => {
      const speedIndex = prevSpeedAlertsCopy.findIndex(
        (speed) =>
          speed.nid === speedAlert.nid && speed.stat === speedAlert.stat
      );
      if (speedIndex > -1) {
        // Update existing speed item
        const updatedSpeeds = [...prevSpeedAlertsCopy];
        const hasId = updatedSpeeds[speedIndex]._id;
        if (hasId) {
          updatedSpeeds[speedIndex] = {
            _id: updatedSpeeds[speedIndex]._id,
            ...speedAlert,
          };
        } else {
          updatedSpeeds[speedIndex] = speedAlert;
        }
        return updatedSpeeds;
      } else {
        // Add new speed item
        return [...prevSpeedAlertsCopy, speedAlert];
      }
    });
  };


  const headers: HeadsType<string, JSX.Element> = useMemo(
  () => [
    {
      content: "PLAYER",
      style: { width: isCC(user) ? "210px" : "318px" },
      class: (isCC(user) ? "l-ll " : "") + "t-ll",
      isOrderly: true,
      isColumnFixedY: true,
    },
    {
      content: "NO.",
      style: { width: isCC(user) ? "70px" : "86px" },
      class: "t-ll",
      isOrderly: true,
      isColumnFixedY: true,
    },
    {
      content: "POS.",
      style: { width: isCC(user) ? "90px" : "86px" },
      class: "t-ll",
      isOrderly: true,
      isColumnFixedY: true,
    },
    ...customHeaders.map((header: any, index) => ({
      content: header.label,
      style: { width: "175px" },
      isOrderly: !editable,
      class: "t-ll",
      print: (content: string) => (
        <Fragment>
          <label>{content}</label>
          {editable && (
            <button
              className="trash-00"
              onClick={() => handleDeleteMetric(index, header.metric)}
            >
              <img src="/assets/trash.svg" alt="" height="20px" />
            </button>
          )}
        </Fragment>
      ),
    })),
  ],
  [customHeaders, editable, user, handleDeleteMetric]
);
  const rows: RowType[] = useMemo(() => {
  if (!currentAlert.players.length) return [];
  const teamIdStr = teamId.toString();
  return currentAlert.players.map((player, index) => ({
    columns: [
      {
        content: {
          name: player.name,
          nameSplited: (player.name || "").split(" ").slice(-1).join(" "),
        },
        class: (isCC(user) ? "l-ll " : "") + "t-ll",
        print: (content) => (
          <label className="l-c">
            <b>{text_truncate(content.name, 40)}</b>
          </label>
        ),
        order: (contentA, contentB) => {
          const isNumberA = !Number.isNaN(+contentA.nameSplited);
          const isNumberB = !Number.isNaN(+contentB.nameSplited);
          if (isNumberA && isNumberB) {
            return +contentB.nameSplited - +contentA.nameSplited;
          }
          return arraySort(contentB.nameSplited, contentA.nameSplited);
        },
      },
      {
        content: player.jerseyNumber.toString(),
        class: "t-ll",
        print: (content) => <label className="l-c rr-pp">#{content}</label>,
        order: (contentA: string, contentB: string) => {
          const jerseyA = contentA.replace(/\D/g, "");
          const jerseyB = contentB.replace(/\D/g, "");
          const jerseyLetterA = contentA.replace(jerseyA, "");
          const jerseyLetterB = contentB.replace(jerseyB, "");

          if (+jerseyA !== +jerseyB) return +jerseyB - +jerseyA;
          return jerseyLetterB.localeCompare(jerseyLetterA);
        },
      },
      {
        content: player.position,
        class: "t-ll",
        print: () => (
          <label className="l-c rr-pp">{player?.position}</label>
        ),
      },
      ...(player.alerts ?? []).map((alert: any) => ({
        content: alert.value,
        style: { width: "175px" },
        class: "tt-qq-0-alert t-ll",
        print: () => (
          <EditableAlerts
            key={`alert-${player.id}-${alert.type}`}
            playerId={player.id}
            editable={editable}
            alert={alert}
            alertType={alert.type}
            currentAlert={currentAlert}
            updateAlert={updateAlert}
            updateAlertStatus={saveUpdatedData}
            updateSpeed={alert.type === "speedMPH" ? addOrUpdateSpeed : undefined}
            teamId={teamIdStr}
          />
        ),
      })),
    ],
    id: player.id + index,
  }));
}, [currentAlert.players, editable, user, teamId, updateAlert, saveUpdatedData, addOrUpdateSpeed]);

  return (
    <div>
      <div
        className="manage-btn"
        style={{
          marginLeft: "25px",
          marginBottom: "12px",
          position: "relative",
        }}
      >
        <div className={"switch-00"}>
          <label
            className="label-009"
            onClick={() => {
              handleEditingChange();
            }}
          >
            {"Editing " + (editable ? "Unlocked" : "Locked")}
          </label>
          <input
            type="checkbox"
            id="editing-practice-manager"
            name="set-name"
            className="switch-input"
            checked={editable}
            onChange={() => handleEditingChange()}
          />
          <label
            htmlFor="editing-practice-manager"
            id="editing-practice-manager-label"
            className="switch-label"
          />
        </div>
        <button
          className={"btn-green"}
          style={
            editable
              ? {}
              : { opacity: 0.5, cursor: "default", pointerEvents: "none" }
          }
          onClick={handleSaveTableAlert}
        >
          Save
        </button>
      </div>
      <CustomTableFilter
        show={showCustomFilter === 1}
        close={closeCustom}
        open={() => setShowCustomFilter(1)}
        forceScope={"LIVE_ALERTING"}
        customLoad={currentAlert.headers}
        setShowFilter={showCustomFilter}
        liveTab={true}
        alerting={true}
        editable={editable}
        closeAlert={closeAlert}
        accountPage={accountPage}
      />

      <div
        style={accountPage ? {
          height: "calc(100vh - 248px)",
          overflowX: "auto",
          overflowY: "auto",
        }: {overflowX: "auto"} }
      >
        <Table
          classTable="condensed-view"
          forceScope={"LIVE_ALRETING_TAB"}
          headers={headers}
          rows={rows}
          default={{ orderColumn: 2 }}
          alerting={true}
          customRows={currentAlert.headers.length}
        />
      </div>
    </div>
  );
};
